import autobind from "autobind-decorator";

export class ValidationError {
  constructor(message, errorSource) {
    Object.assign(this, { message, errorSource });
  }
}

function convertToPromise(validation, errorSource) {
  let result;

  try {
    result = validation();
  } catch (error) {
    if (error.constructor !== ValidationError) {
      console.error(error); // eslint-disable-line no-console
      return Promise.reject(error);
    }

    return Promise.reject(new ValidationError(error.message, errorSource));
  }

  if (!result) return Promise.resolve();
  if (result.then) return result;

  throw new ValidationError(
    "Could not convert to promise. Please return falsy value, a string, or a promise."
  );
}

/**
 * @param {Array|String} [skip] An array of validations to skip
 */
async function valid({ skip = [] } = {}) {
  /* eslint-disable no-restricted-syntax */
  let currentPromise;
  let skipArray = skip;

  if (skipArray.constructor.name === "String") skipArray = [skipArray];

  for (const validation of this.validations) {
    if (skipArray.includes(validation)) continue; // eslint-disable-line no-continue
    const method = this[validation];
    // eslint-disable-next-line no-await-in-loop
    currentPromise = await convertToPromise(method, this.constructor);
  }

  return currentPromise;
} /* eslint-enable no-restricted-syntax */

/**
 * A lazyValidated method may return a falsy value if it's valid, throw a new Error if it's invalid,
 * or return a promise if it's an asynchronous validation. Reject the promise to fail the validation
 * or resolve the promise to pass the validation.
 * Adds a valid() instance method to your class. When this.valid() is called all methods with
 * the lazyValidate decorator are called in order (top to bottom).  Validations are performed
 * lazily, meaning that each validation won't
 * run until the previous is successful. If all validations are successful, valid() returns a
 * resolved promise with no value. If a validation fails, valid() returns a rejected promise with
 * ValidationError.message being the returned string of the failed validation.
 * @returns {void}
 */
export function lazyValidate(target, propertyKey, descriptor) {
  const classPrototype = target;

  classPrototype.valid = classPrototype.valid || valid;
  classPrototype.validations = classPrototype.validations || [];
  classPrototype.validations.push(propertyKey);

  return autobind(target, propertyKey, descriptor);
}
